import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Code, HelpCircle, Info, Wrench } from "lucide-react";
import type {
  MissingSchemaDetails,
  MissingSchemaFaq,
  MissingSchemaFix,
  PageIssue,
  ProposedFix,
  SchemaValidationDetails,
} from "@/types/technicalAudit";

// Editable render surface for the missing_schema auto-fix. The generated JSON-LD
// (`schema_jsonld`) is the primary value and the single source of truth that gets
// applied — the reviewer can adjust it before approving. Entity types
// (LocalBusiness/Service/Product/Organization) become applyable via JSON-LD
// injection only when the backend confirms the site's plugin supports it and
// marks the fix `apply_disabled = false` (apply_method "jsonld_injection"); when
// unproven the backend keeps `apply_disabled = true` and this card shows a
// "handed off for manual implementation" state while the drawer hides the Apply
// button. Capability is always backend-driven — never a frontend env check.
// Themed with coral/teal tokens, never raw palette classes.

interface MissingSchemaCardProps {
  issue: PageIssue;
  // Same edit channel every other editable card uses: push the reviewer's value
  // up so Approve/Apply send exactly what is on screen.
  onEdit: (value: ProposedFix) => void;
}

// Pretty-print the JSON-LD whether it arrives as an object or a JSON string.
function prettyJson(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
}

// Read the missing_schema fix payload leniently from the approved value, then the
// generated suggestion (object or JSON string).
function readMissingSchemaFix(issue: PageIssue): MissingSchemaFix | null {
  const candidates = [
    issue.approved_value,
    issue.proposed_fix,
    issue.proposed_value,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    if (typeof raw === "object" && !Array.isArray(raw)) {
      return raw as MissingSchemaFix;
    }
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed as MissingSchemaFix;
        }
      } catch {
        // not a JSON payload — skip
      }
    }
  }
  return null;
}

export function MissingSchemaCard({ issue, onEdit }: MissingSchemaCardProps) {
  const details = (issue.issue_details || {}) as Partial<MissingSchemaDetails>;
  // Memoize so `fix` keeps a stable identity across the drawer's own re-renders
  // (the proposed_value string path would otherwise produce a fresh object every
  // render and re-fire the emit effect). Keyed on `issue` to match the compiler's
  // inferred dependency; `issue` only changes when a different issue is opened.
  const fix = useMemo(() => readMissingSchemaFix(issue), [issue]);

  // schema_validation_error reuses this card to review + edit a CORRECTED JSON-LD
  // block. The validation errors (crawler details + the generator's per-error
  // explanations) are surfaced prominently above the JSON-LD.
  const isSchemaValidation = issue.issue_type === "schema_validation_error";
  const validationDetails = details as Partial<SchemaValidationDetails>;
  const validationErrors: string[] =
    (Array.isArray(fix?.validation_errors) && fix?.validation_errors.length
      ? fix?.validation_errors
      : validationDetails.error_messages) || [];
  const errorExplanations =
    fix && Array.isArray(fix.error_explanations) ? fix.error_explanations : [];
  const schemaTypesPresent: string[] =
    (Array.isArray(fix?.schema_types) && fix?.schema_types.length
      ? fix?.schema_types
      : validationDetails.schema_types) || [];

  const recommendedSchemaType = fix?.recommended_schema_type || "";
  const pageIntent = fix?.page_intent || details.page_intent || "";
  const reasoning = fix?.reasoning;
  const recommendation = fix?.recommendation;
  const applyMethod = fix?.apply_method;
  const applyDisabled = fix?.apply_disabled === true;
  const applyDisabledReason = fix?.apply_disabled_reason;
  const capabilityGap = fix?.capability_gap;
  const faqs: MissingSchemaFaq[] = fix && Array.isArray(fix.faqs) ? fix.faqs : [];
  const isFaqPage =
    recommendedSchemaType.toLowerCase() === "faqpage" || faqs.length > 0;

  const initialJson = prettyJson(fix?.schema_jsonld);
  const hasStoredSuggestion = Boolean(
    issue.approved_value || issue.proposed_fix || issue.proposed_value,
  );
  const hasFix = Boolean(fix && (initialJson || recommendedSchemaType));

  // Local draft, keyed on the stored suggestion so a regenerate/reopen resets it.
  const draftKey = `${issue.id}:${issue.issue_type}:${JSON.stringify(
    issue.approved_value ?? issue.proposed_fix ?? "",
  )}`;
  const [draft, setDraft] = useState<{ key: string; json: string } | null>(
    null,
  );
  const editedJson = draft?.key === draftKey ? draft.json : initialJson;

  // Parse the edited JSON-LD; surface a clear error and keep the last valid value
  // emitted (we never push broken JSON up to Apply).
  const parsed = useMemo<{ value: unknown; error: string | null }>(() => {
    const trimmed = editedJson.trim();
    if (!trimmed) return { value: undefined, error: null };
    try {
      return { value: JSON.parse(trimmed), error: null };
    } catch (e) {
      return {
        value: undefined,
        error: e instanceof Error ? e.message : "Invalid JSON",
      };
    }
  }, [editedJson]);

  // Push the edited fix up so Approve/Apply send exactly what the reviewer sees.
  // Skip the emit on a parse error so a broken draft never reaches the apply path.
  useEffect(() => {
    if (!fix) return;
    if (parsed.error) return;
    const value: MissingSchemaFix = {
      ...fix,
      schema_jsonld: parsed.value as Record<string, unknown> | undefined,
    };
    onEdit(value as ProposedFix);
  }, [parsed, fix, onEdit]);

  if (!hasFix) {
    const actionLabel = hasStoredSuggestion ? "Regenerate fix" : "Generate fix";
    return (
      <div className="space-y-4">
        {isSchemaValidation && validationErrors.length > 0 && (
          <ValidationErrorBlock
            errors={validationErrors}
            schemaTypes={schemaTypesPresent}
            explanations={[]}
          />
        )}
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            Click <span className="font-medium">{actionLabel}</span> above to{" "}
            {isSchemaValidation
              ? "generate a corrected structured-data (JSON-LD) block that resolves the validation errors above, then review and edit it before applying."
              : "draft page-appropriate structured data (JSON-LD) for this page, then review and edit it before applying."}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Validation errors — the most prominent block for schema_validation_error */}
      {isSchemaValidation && validationErrors.length > 0 && (
        <ValidationErrorBlock
          errors={validationErrors}
          schemaTypes={schemaTypesPresent}
          explanations={errorExplanations}
        />
      )}

      {/* Summary banner — recommended schema type + page intent */}
      <div className="flex items-start gap-3 rounded-xl border border-teal/40 bg-teal-light p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-teal/20">
          <Code className="h-5 w-5 text-teal-dark" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-teal-dark">
            {isSchemaValidation ? "Corrected schema: " : "Recommended schema: "}
            {recommendedSchemaType || "Structured data"}
          </h4>
          <p className="mt-1 text-sm text-teal-dark/90">
            {isSchemaValidation ? (
              <>
                The corrected JSON-LD below resolves the validation errors while
                preserving the real facts already on the page.
              </>
            ) : (
              <>
                This page looks like{" "}
                <span className="font-medium">
                  {pageIntent || "a key page"}
                </span>{" "}
                content. Adding matching structured data can improve entity
                clarity and rich-result eligibility.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Handoff state — entity types with no safe live-write path */}
      {applyDisabled && (
        <div className="flex items-start gap-3 rounded-xl border border-coral/40 bg-coral-light p-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-coral/20">
            <Wrench className="h-5 w-5 text-coral-dark" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-coral-dark">
              Handed off for manual implementation
            </h4>
            <p className="mt-1 text-sm text-coral-dark/90">
              {applyDisabledReason ||
                "This schema type has no safe automatic write path, so it cannot be applied from here."}
            </p>
            {capabilityGap && (
              <p className="mt-2 text-xs text-coral-dark/80">
                <span className="font-medium">Why:</span> {capabilityGap}
              </p>
            )}
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-light">
              <Info className="h-4 w-4 text-teal-dark" />
            </div>
            Schema Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border bg-warm-bg p-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Schema type
              </dt>
              <dd className="mt-1 text-sm font-semibold text-foreground">
                {recommendedSchemaType || "—"}
              </dd>
            </div>
            <div className="rounded-lg border bg-warm-bg p-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                Page intent
              </dt>
              <dd className="mt-1 text-sm font-semibold text-foreground">
                {pageIntent || "—"}
              </dd>
            </div>
            {applyMethod && (
              <div className="rounded-lg border bg-warm-bg p-3 sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  Apply method
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">
                  {applyMethod === "yoast_article_meta"
                    ? "Yoast article meta (SEO plugin)"
                    : applyMethod === "yoast_faq_block"
                      ? "Yoast FAQ block"
                      : applyMethod === "jsonld_injection"
                        ? "Structured data injection (JSON-LD)"
                        : "Manual handoff"}
                </dd>
              </div>
            )}
          </dl>

          {/* Editable JSON-LD — the primary value / source of truth */}
          <div>
            <div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-teal-dark">
              <Code className="h-3.5 w-3.5" />
              Generated JSON-LD (editable)
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              Review and edit the structured data below before applying. This
              JSON-LD is the source of truth that gets written to the page.
            </p>
            <Textarea
              value={editedJson}
              onChange={(e) => setDraft({ key: draftKey, json: e.target.value })}
              spellCheck={false}
              className="min-h-[220px] font-mono text-xs leading-relaxed"
              placeholder="Edit the generated JSON-LD..."
            />
            {parsed.error && (
              <p className="mt-2 text-xs text-destructive">
                Invalid JSON — fix the syntax so this can be applied:{" "}
                {parsed.error}
              </p>
            )}
          </div>

          {/* FAQ preview (read-only — JSON-LD above stays the source of truth) */}
          {isFaqPage && faqs.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <HelpCircle className="h-3.5 w-3.5" />
                FAQs in this schema ({faqs.length})
              </div>
              <div className="space-y-2">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border bg-warm-bg p-3 text-sm"
                  >
                    <p className="font-medium text-foreground">
                      {faq.question}
                    </p>
                    <p className="mt-1 text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                These mirror the JSON-LD above — edit the JSON-LD to change them.
              </p>
            </div>
          )}

          {(reasoning || recommendation) && (
            <div className="rounded-md bg-teal-light p-3">
              {recommendation && (
                <p className="text-xs font-medium text-teal-dark">
                  {recommendation}
                </p>
              )}
              {reasoning && (
                <p className="mt-1 text-xs text-teal-dark/90">{reasoning}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Prominent validation-error surface for schema_validation_error issues. Lists the
// crawl-reported errors and, when the fix has been generated, the model's per-error
// explanation of what the corrected JSON-LD changed. Themed with destructive/coral
// tokens, never raw palette classes.
function ValidationErrorBlock({
  errors,
  schemaTypes,
  explanations,
}: {
  errors: string[];
  schemaTypes: string[];
  explanations: { error?: string; fix?: string }[];
}) {
  const fixes = explanations.filter((e) => e && (e.error || e.fix));
  return (
    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/15">
          <AlertTriangle className="h-5 w-5 text-destructive" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-destructive">
            Structured-data validation{" "}
            {errors.length === 1 ? "error" : `errors (${errors.length})`}
          </h4>
          {schemaTypes.length > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Affected schema: {schemaTypes.join(", ")}
            </p>
          )}
          <ul className="mt-2 space-y-1">
            {errors.map((err, idx) => (
              <li
                key={idx}
                className="flex gap-2 text-sm text-foreground"
              >
                <span className="text-destructive">•</span>
                <span>{err}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {fixes.length > 0 && (
        <div className="mt-3 rounded-lg border border-destructive/20 bg-warm-bg p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What the corrected JSON-LD fixes
          </div>
          <ul className="space-y-2">
            {fixes.map((item, idx) => (
              <li key={idx} className="text-sm">
                {item.error && (
                  <p className="font-medium text-foreground">{item.error}</p>
                )}
                {item.fix && (
                  <p className="mt-0.5 text-muted-foreground">{item.fix}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
